import React, { useCallback, useEffect, useState } from "react";
import { productsAPI, variantGroupsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { productImageUrl } from "../pos/utils/images";
import Modal from "./Modal";
import ProductForm from "./ProductForm";
import RangeForm from "./RangeForm";
import ApplyToMembersDialog from "./ApplyToMembersDialog";
import { useMembership } from "../context/MembershipContext";

const smallInput =
  "w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500";

/** The variant's own picture in a small box; a placeholder when it has none. */
const MemberPicture = ({ member }) => {
  const src = productImageUrl(member.images?.[0] || member.image_url);

  return (
    <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center text-lg">
      {src ? (
        <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <span aria-hidden="true">🖼️</span>
      )}
    </div>
  );
};

/**
 * One range opened up: its variants with their stock, a quick way to add more
 * of them, and the actions that touch the whole range. Adding variants is the
 * main event — a pasted list of flavours or colours becomes products in one go.
 */
const RangeDetail = ({ groupId, onClose, onChanged }) => {
  const { t } = useI18n();
  const { can, shop } = useMembership();
  const warehouseOn = Boolean(shop?.warehouse_enabled);
  const currency = useShopCurrency();
  const [range, setRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [labels, setLabels] = useState("");
  const [stockEdits, setStockEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [editingRange, setEditingRange] = useState(false);
  const [applying, setApplying] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await variantGroupsAPI.get(groupId);
      setRange(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || t("ranges.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const fail = (err, fallback) =>
    setError(
      err.response?.data?.message || err.response?.data?.error || t(fallback),
    );

  const close = () => {
    onChanged();
    onClose();
  };

  const addVariants = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");

    try {
      const { data } = await variantGroupsAPI.addMembers(groupId, labels);
      setLabels("");
      setNotice(
        t("ranges.detail.added", {
          added: data.created.length,
          skipped: data.skipped.length,
        }),
      );
      await load();
    } catch (err) {
      fail(err, "ranges.detail.addFailed");
    } finally {
      setBusy(false);
    }
  };

  const changedStock = (range?.members || []).filter((member) => {
    const edit = stockEdits[member.id];
    return edit !== undefined && edit !== "" && Number(edit) !== member.stock_quantity;
  });

  // Stock moves are deltas so each one lands in the movement history; the
  // owner types what is on the shelf and the difference is worked out here.
  const saveStock = async () => {
    setBusy(true);
    setError("");
    setNotice("");

    try {
      for (const member of changedStock) {
        await productsAPI.updateStock(
          member.id,
          Number(stockEdits[member.id]) - member.stock_quantity,
          t("ranges.detail.stockNote"),
        );
      }
      setStockEdits({});
      setNotice(t("ranges.detail.stockSaved", { count: changedStock.length }));
      await load();
    } catch (err) {
      fail(err, "ranges.detail.stockFailed");
    } finally {
      setBusy(false);
    }
  };

  const removeFromRange = async (member) => {
    if (!window.confirm(t("ranges.detail.removeConfirm", { name: member.name }))) {
      return;
    }

    try {
      await productsAPI.update(member.id, { variant_group_id: "" });
      await load();
    } catch (err) {
      fail(err, "ranges.detail.removeFailed");
    }
  };

  const deleteRange = async () => {
    if (
      !window.confirm(
        t("ranges.detail.deleteConfirm", { count: range.members.length }),
      )
    ) {
      return;
    }

    try {
      await variantGroupsAPI.delete(groupId);
      close();
    } catch (err) {
      fail(err, "ranges.detail.deleteFailed");
    }
  };

  if (loading) {
    return (
      <Modal title="…" onClose={onClose}>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-primary-600" />
        </div>
      </Modal>
    );
  }

  if (!range) {
    return (
      <Modal title={t("ranges.title")} onClose={onClose}>
        <p className="text-red-600">{error}</p>
      </Modal>
    );
  }

  const image = productImageUrl(range.images?.[0] || range.image_url);

  return (
    <>
      <Modal title={range.name} onClose={close}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-20 h-20 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
              {image ? (
                <img src={image} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl">🗂️</span>
              )}
            </div>
            <div className="flex-1 min-w-0 text-sm text-gray-600">
              <p>
                {[range.brand, range.category].filter(Boolean).join(" · ")}
              </p>
              <p className="font-semibold text-gray-800">
                {formatMoney(range.price, currency)}
                {range.cost_price !== null && range.cost_price !== undefined && (
                  <span className="font-normal text-gray-500">
                    {" · "}
                    {t("ranges.detail.buyPrice", {
                      amount: formatMoney(range.cost_price, currency),
                    })}
                  </span>
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {can("products.edit") && (
                  <>
                <button
                  type="button"
                  onClick={() => setEditingRange(true)}
                  className="px-4 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium"
                >
                  {t("ranges.detail.edit")}
                </button>
                <button
                  type="button"
                  disabled={range.members.length === 0}
                  onClick={() => setApplying(true)}
                  className="px-4 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium disabled:opacity-40"
                >
                  {t("ranges.detail.apply")}
                </button>
                  </>
              )}
              {can("products.delete") && (
                <button
                  type="button"
                  onClick={deleteRange}
                  className="px-4 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 font-medium"
                >
                  {t("ranges.detail.delete")}
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {notice && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
              {notice}
            </div>
          )}

          {can("products.edit") && (
            <form onSubmit={addVariants} className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800">
                {t("ranges.detail.addTitle")}
              </label>
              <textarea
                rows={4}
                value={labels}
                onChange={(e) => setLabels(e.target.value)}
                placeholder={t("ranges.detail.addPlaceholder")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500">{t("ranges.detail.addHint")}</p>
              <button
                type="submit"
                disabled={busy || !labels.trim()}
                className="px-5 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-50"
              >
                {t("ranges.detail.addButton")}
              </button>
            </form>
          )}

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("ranges.detail.variants")}
              </h3>
              {changedStock.length > 0 && can("products.stock") && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={saveStock}
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
                >
                  {t("ranges.detail.saveStock", { count: changedStock.length })}
                </button>
              )}
            </div>

            {range.members.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-300 rounded-lg">
                {t("ranges.detail.noVariants")}
              </p>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 w-14">
                        <span className="sr-only">{t("ranges.detail.colPicture")}</span>
                      </th>
                      <th className="px-3 py-2 text-start">
                        {t("ranges.detail.colVariant")}
                      </th>
                      <th className="px-3 py-2 text-start">
                        {t("ranges.detail.colPrice")}
                      </th>
                      <th className="px-3 py-2 text-center">
                        {t(warehouseOn ? "ranges.detail.colShopStock" : "ranges.detail.colStock")}
                      </th>
                      {warehouseOn && (
                        <th className="px-3 py-2 text-center">
                          {t("ranges.detail.colWarehouseStock")}
                        </th>
                      )}
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {range.members.map((member) => (
                      <tr key={member.id}>
                        <td className="px-3 py-2">
                          <MemberPicture member={member} />
                        </td>
                        <td className="px-3 py-2">
                          <p className="font-semibold text-gray-800">
                            {member.variant_label}
                            {member.is_draft && (
                              <span
                                title={t("products.draftHint")}
                                className="ms-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold align-middle"
                              >
                                {t("products.draft")}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">{member.name}</p>
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {member.is_draft && Number(member.price) <= 0
                            ? t("products.noPrice")
                            : formatMoney(member.price, currency)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="number"
                            min="0"
                            value={stockEdits[member.id] ?? member.stock_quantity}
                            onChange={(e) =>
                              setStockEdits({
                                ...stockEdits,
                                [member.id]: e.target.value,
                              })
                            }
                            aria-label={t("ranges.detail.stockOf", {
                              name: member.variant_label,
                            })}
                            className={smallInput}
                          />
                        </td>
                        {warehouseOn && (
                          <td className="px-3 py-2 text-center tabular-nums text-gray-600">
                            {member.warehouse_quantity ?? 0}
                          </td>
                        )}
                        <td className="px-3 py-2 text-end whitespace-nowrap">
                          {can("products.edit") && (
                            <>
                            <button
                              type="button"
                              onClick={() => setEditingProduct(member)}
                              className="px-3 py-1.5 rounded-lg text-blue-600 hover:bg-blue-50"
                            >
                              {t("ranges.detail.editProduct")}
                            </button>
                            <button
                              type="button"
                              onClick={() => removeFromRange(member)}
                              className="px-3 py-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                            >
                              {t("ranges.detail.remove")}
                            </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {editingRange && (
        <RangeForm
          group={range}
          onClose={() => setEditingRange(false)}
          onSaved={() => {
            setEditingRange(false);
            load();
          }}
        />
      )}

      {applying && (
        <ApplyToMembersDialog
          group={range}
          memberCount={range.members.length}
          onClose={() => setApplying(false)}
          onApplied={(updated) => {
            setApplying(false);
            setNotice(t("ranges.apply.done", { count: updated }));
            load();
          }}
        />
      )}

      {editingProduct && (
        <ProductForm
          product={editingProduct}
          onClose={() => {
            setEditingProduct(null);
            load();
          }}
        />
      )}
    </>
  );
};

export default RangeDetail;
